import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarX2 } from 'lucide-react';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { ApiError } from '../lib/api';
import { useI18n, type TranslationKey } from '../lib/i18n';
import { formatDateTime } from '../lib/format';
import { Badge, Button, Card, Field, LoadingBlock, Textarea, useToast, type BadgeTone } from '../components/ui';
import { MeetingFacts, PublicHeader, Unavailable, call, unavailableReason, type GuestBooking } from './Meet';

const STATUS_TONE: Record<GuestBooking['meeting']['status'], BadgeTone> = {
  SCHEDULED: 'success',
  STARTED: 'info',
  IN_PROGRESS: 'info',
  DONE: 'neutral',
  CANCELLED: 'error',
};

/**
 * A guest's own page of the meeting they booked through a booking link (2026-11-05),
 * through the link their email carries: when, how long, where — the meeting's link
 * for an online one — and with whom. Until it starts they cancel it here, with a
 * reason if they like; the person they booked is told.
 */
export function BookingPage() {
  const { token = '' } = useParams();
  const { t, locale } = useI18n();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [asking, setAsking] = useState(false);
  const [note, setNote] = useState('');

  const booking = useQuery({
    queryKey: ['booking', token],
    queryFn: () => call<GuestBooking>(`bookings/${token}`),
    retry: false,
  });

  const cancel = useMutation({
    mutationFn: () => call<GuestBooking>(`bookings/${token}/cancel`, { method: 'POST', body: JSON.stringify({ note: note.trim() || undefined }) }),
    onSuccess: (next) => {
      queryClient.setQueryData(['booking', token], next);
      setAsking(false);
      const host = (locale === 'ar' && next.host.fullNameAr) || next.host.fullName;
      toast.success(t('booking.cancelledToast', { name: host }));
    },
    onError: (e: unknown) => {
      void queryClient.invalidateQueries({ queryKey: ['booking', token] });
      setAsking(false);
      toast.error(
        e instanceof ApiError && e.code === 'MEETING_BOOKING_REFUSED' ? t('booking.cannotCancel') : t('share.unavailable.tryAgain'),
      );
    },
  });

  const data = booking.data;
  const host = data ? (locale === 'ar' && data.host.fullNameAr) || data.host.fullName : '';
  const company = data ? (locale === 'ar' ? data.company.nameAr : data.company.name) : '';

  return (
    <div className="min-h-full bg-[var(--surface-page)]">
      <PublicHeader />
      <main className="mx-auto max-w-5xl px-4 py-8">
        {booking.isLoading ? (
          <LoadingBlock />
        ) : !data ? (
          <Unavailable reason={unavailableReason(booking.error)} />
        ) : (
          <Card className="mx-auto max-w-2xl">
            <p className="text-mono-sm uppercase tracking-widest text-[var(--text-tertiary)]">{t('booking.kicker')}</p>
            <div className="mt-1 mb-5 flex flex-wrap items-center gap-x-3 gap-y-1">
              <h1 className="text-h2 text-[var(--text-primary)]">{t('meet.with', { name: host })}</h1>
              <Badge tone={STATUS_TONE[data.meeting.status]}>{t(`booking.status.${data.meeting.status}` as TranslationKey)}</Badge>
            </div>

            {data.meeting.status === 'CANCELLED' && (
              <div className="mb-5 rounded-md border border-error/40 bg-error-bg px-4 py-2.5 text-body-sm text-error-fg dark:bg-error/12 dark:text-error-dark">
                {data.meeting.cancelledBy === 'GUEST'
                  ? t('booking.cancelledByYou', { when: formatDateTime(data.meeting.cancelledAt, locale) })
                  : t('booking.cancelledByHost', { name: host, when: formatDateTime(data.meeting.cancelledAt, locale) })}
                {data.meeting.cancelNote && (
                  <span className="mt-1 block whitespace-pre-wrap" dir="auto">
                    “{data.meeting.cancelNote}”
                  </span>
                )}
              </div>
            )}

            <MeetingFacts booking={data} />

            <dl className="mt-5 grid gap-1 border-t hairline pt-4 text-body-sm">
              <div className="flex flex-wrap gap-x-2">
                <dt className="text-faint">{t('booking.bookedBy')}</dt>
                <dd className="text-[var(--text-primary)]">
                  <bdi>{data.guest.name}</bdi> · <span dir="ltr">{data.guest.email}</span>
                </dd>
              </div>
              {data.guest.note && (
                <div className="flex flex-wrap gap-x-2">
                  <dt className="text-faint">{t('booking.yourNote')}</dt>
                  <dd className="whitespace-pre-wrap text-[var(--text-primary)]" dir="auto">
                    {data.guest.note}
                  </dd>
                </div>
              )}
            </dl>

            {data.canCancel &&
              (asking ? (
                <div className="mt-6 rounded-md border hairline bg-[var(--surface-sunken)] p-4">
                  <h2 className="mb-3 text-h3 text-[var(--text-primary)]">{t('booking.cancelTitle')}</h2>
                  <Field label={t('booking.cancelNote')} hint={t('booking.cancelNoteHint', { name: host })}>
                    <Textarea value={note} maxLength={1000} dir="auto" onChange={(e) => setNote(e.target.value)} />
                  </Field>
                  <div className="mt-4 flex flex-wrap justify-end gap-2">
                    <Button onClick={() => setAsking(false)} disabled={cancel.isPending}>
                      {t('booking.keep')}
                    </Button>
                    <Button variant="danger" loading={cancel.isPending} onClick={() => cancel.mutate()}>
                      {t('booking.cancelConfirm')}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="mt-6 flex justify-end">
                  <Button variant="danger" icon={<CalendarX2 className="h-4 w-4" aria-hidden />} onClick={() => setAsking(true)}>
                    {t('booking.cancel')}
                  </Button>
                </div>
              ))}

            <p className="mt-8 text-center text-caption text-faint">{t('meet.footer', { company })}</p>
          </Card>
        )}
      </main>
    </div>
  );
}
